import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { adminApi } from "../lib/api";
import { TEAMS } from "../lib/site";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { useToast } from "../hooks/use-toast";

export default function AdminOdds({ token, matches, onChanged }) {
  const { toast } = useToast();
  const [edits, setEdits] = useState({});
  const [auto, setAuto] = useState(false);

  useEffect(() => {
    adminApi.getAutoOdds(token).then((r) => setAuto(r.enabled)).catch(() => {});
  }, [token]);

  const open = matches.filter((m) => m.status !== "completed");

  const getVal = (m, mk, o) => {
    const e = edits[m.id]?.[mk.key]?.[o.id];
    if (e !== undefined) return e;
    const n = o.odds ?? o.back ?? o.lay;
    return n != null ? Number(n).toFixed(2) : "";
  };

  const setVal = (mId, mkKey, oId, v) => {
    setEdits((prev) => ({
      ...prev,
      [mId]: { ...prev[mId], [mkKey]: { ...prev[mId]?.[mkKey], [oId]: v } },
    }));
  };

  const save = async (m) => {
    const markets = (m.markets || []).map((mk) => ({
      key: mk.key,
      options: mk.options
        .map((o) => {
          const val = Number(getVal(m, mk, o));
          return val && val >= 1.01 ? { id: o.id, odds: val } : null;
        })
        .filter(Boolean),
    })).filter((mk) => mk.options.length > 0);
    try {
      await adminApi.updateOdds(token, m.id, markets);
      toast({ title: "Odds updated ✅", description: "Users see the new odds live." });
      setEdits((prev) => ({ ...prev, [m.id]: undefined }));
      onChanged && onChanged();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || "Try again." });
    }
  };

  const toggleAuto = async (v) => {
    setAuto(v);
    try {
      await adminApi.setAutoOdds(token, v);
      toast({
        title: v ? "Live odds movement ON" : "Live odds movement OFF",
        description: v ? "Odds drift automatically in real time." : "Odds stay fixed until you edit them.",
      });
    } catch (e) {
      setAuto(!v);
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between rounded-2xl border border-primary/25 bg-primary/5 p-4 mb-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-5 w-5 text-primary" />
          <div>
            <div className="font-display font-semibold text-sm">Live odds movement</div>
            <div className="text-xs text-muted-foreground">Auto-drift odds in real time so users see live changes.</div>
          </div>
        </div>
        <Switch checked={auto} onCheckedChange={toggleAuto} />
      </div>

      <div className="space-y-3">
        {open.length === 0 && (
          <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground">No open matches to set odds for.</div>
        )}
        {open.map((m) => (
          <div key={m.id} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-medium">
                {m.homeTeam?.name || TEAMS[m.home]?.name || m.home} vs {m.awayTeam?.name || TEAMS[m.away]?.name || m.away}
                <span className="text-xs text-muted-foreground"> · {m.status}</span>
              </div>
              <Button size="sm" className="font-semibold" onClick={() => save(m)}>Save Odds</Button>
            </div>
            <div className="space-y-4">
              {(m.markets || []).map((mk) => (
                <div key={mk.key}>
                  <div className="text-xs font-semibold text-muted-foreground mb-2">{mk.name}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {mk.options.map((o) => (
                      <div key={o.id} className="flex items-center gap-2">
                        <span className="text-sm flex-1 truncate">{o.label}</span>
                        <Input value={getVal(m, mk, o)} onChange={(e) => setVal(m.id, mk.key, o.id, e.target.value)} className="w-24 h-9" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
